import React, { useRef } from 'react'
import { Input, FormFeedback } from 'reactstrap'
import {
  Control,
  FieldValues,
  Path,
  useController,
} from 'react-hook-form'

import ICSButton from '../ICS/Button/ICSButton'
import ICSToolTip from '../ICSToolTip/ICSToolTip'

const noFileChosenText = 'No File Chosen'

const toSafeId = (value: string) => value.replace(/\W/g, '-')

type FileExtension = `.${string}`

type ICSFileInputProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>
  control: Control<TFieldValues>
  label: string
  helpText: string
  accept: FileExtension[]
  className?: string
  multiple?: boolean
}

const ICSFileInput = <
  TFieldValues extends FieldValues,
>({
  name,
  label,
  control,
  helpText,
  accept,
  className,
  multiple = false,
}: ICSFileInputProps<TFieldValues>) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const {
    field: { onBlur, onChange, name: fieldName, value },
    fieldState: { error, invalid },
  } = useController({
    name,
    control,
  })

  const selectedFiles = Array.isArray(value)
    ? value.filter((file): file is File => file instanceof File)
    : []
  const selectedFileNames = selectedFiles.length > 0
    ? selectedFiles.map((file) => file.name).join(', ')
    : noFileChosenText
  const acceptedFileTypes = accept.join(',')
  const tooltipId = `${toSafeId(fieldName)}ToolTip`

  const handleButtonClick = () => {
    inputRef.current?.click()
  }

  return (
    <div className={className}>
      <div className="tooltip-wrapper">
        <ICSButton onClick={handleButtonClick}>
          {label}
        </ICSButton>
        <ICSToolTip
          id={tooltipId}
          text={helpText}
          target={`#${tooltipId}`}
        />
      </div>
      <div style={{fontSize: '12px', color: '#495057', fontStyle: 'italic'}}>{selectedFileNames}</div>
      <Input
        data-testid='file-input'
        onBlur={onBlur}
        onChange={(event) => {
          const files = Array.from(event.target?.files ?? [])
          onChange(files)
        }}
        type="file"
        multiple={multiple}
        valid={!invalid && selectedFiles.length > 0}
        hidden
        invalid={invalid}
        className='text-dark'
        accept={acceptedFileTypes}
        innerRef={inputRef}
      />

      {error?.message && (
        <FormFeedback
          valid={false}
          role="alert"
        >
          {error.message}
        </FormFeedback>
      )}
    </div>
  )
}

export default ICSFileInput
